import { Button, Table, addToast, showDialogConfirm } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import Loading from '../../../../../components/info/Loading';
import SectionTitle from '../../../../../components/text/SectionTitle';
import TableEmptyContent from '../../../../../components/text/TableEmptyContent';

import {
  CollectionFeeOverview,
  CollectionFeeRuleItem,
  deleteCollectionFeeRule,
  getCollectionFees,
} from './api';
import { ConfigModal } from './components/ConfigModal';
import { useRuleColumns } from './hooks/useRuleColumns';

const dictPrefix = 'financialPortal.settings.collectionFees';

export default function CollectionFeesSection() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<CollectionFeeOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<CollectionFeeRuleItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await getCollectionFees();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.loadError`),
        });
      } else {
        setOverview(data);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const handleSaved = useCallback(() => {
    setIsModalOpen(false);
    setEditing(null);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleNew = useCallback(() => {
    setEditing(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((item: CollectionFeeRuleItem) => {
    setEditing(item);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (item: CollectionFeeRuleItem) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${dictPrefix}.confirmDelete.title`),
        subtitle: t(`${dictPrefix}.confirmDelete.subtitle`),
        btnConfirm: {
          text: t(`${dictPrefix}.confirmDelete.confirm`),
          onConfirm: async () => {
            const { error } = await deleteCollectionFeeRule(item.id);
            if (error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t(`${dictPrefix}.deleteError`),
              });
              return;
            }
            addToast({
              icon: 'success',
              color: 'success',
              text: t(`${dictPrefix}.deleteSuccess`),
            });
            setRefreshToken((prev) => prev + 1);
          },
        },
        btnClose: { text: t(`${dictPrefix}.confirmDelete.cancel`) },
      });
    },
    [t],
  );

  const columns = useRuleColumns({ onEdit: handleEdit, onDelete: handleDelete });

  if (isLoading) {
    return <Loading insideCard />;
  }
  if (!overview) {
    return null;
  }

  return (
    <>
      <Row className="align-items-start">
        <Col>
          <SectionTitle text={t(`${dictPrefix}.title`)} />
          <p>{t(`${dictPrefix}.subtitle`)}</p>
        </Col>
        <Col xs="auto">
          <Button
            text={t(`${dictPrefix}.newConfig`)}
            onClick={handleNew}
            size="sm"
            className="mb-1"
            outlined
          />
        </Col>
      </Row>
      <Table
        columns={columns}
        data={overview.items}
        noResultsText={
          <>
            <TableEmptyContent
              title={t(`${dictPrefix}.empty.title`)}
              subtitle={t(`${dictPrefix}.empty.subtitle`)}
            />
            <Button
              text={t(`${dictPrefix}.empty.action`)}
              onClick={handleNew}
              outlined
              size="sm"
              className="mx-auto mb-4"
            />
          </>
        }
      />
      <ConfigModal
        isOpen={isModalOpen}
        item={editing}
        onClose={() => setIsModalOpen(false)}
        onSaved={handleSaved}
      />
    </>
  );
}
